import patientListService from '@/services/patientListService';
import axios from 'axios';
import { CLOUD_SERVICE_BASE_URL_V2 } from '@/utils/const';
import store from '@/store';
import { attachTokenRefresh } from '@/utils/authInterceptors';

// Create axios instance for order API v2
const orderApiV2 = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL_V2 + (process.env.VUE_APP_ENV === "production"
    ? "/portal/order"
    : "/portal/order-staging"
  )
});

orderApiV2.interceptors.request.use(
  async function(config) {
    let token = store?.state?.login?.token || "";
    config.headers.Authorization = token ? `Bearer ${token}` : "";
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

attachTokenRefresh(orderApiV2);

/**
 * get patient list
 * @param {Object} params - query parameters
 * @param {string} params.page - page number
 * @param {string} params.perPage - number of items per page
 * @param {string} params.search_patient_field - search keyword
 * @param {string} params.search_patient_type - search type
 * @param {string} params.sorting_field_input - sorting field input
 * @param {string} params.sorting_order_input - sorting order
 * @returns {Promise} API response
 */
export const getPatientListApi = async (params = {}) => {
  const defaultParams = {
    kit_status: [],
    report_status: [],
    order_type: [],
    start_date: "",
    end_date: "",
    issue: [],
    flagged: false,
    page: "1",
    perPage: "10",
    search_patient_field: "",
    search_patient_type: "patient_name",
    sorting_field_input: "patient_service_date",
    sorting_order_input: "desc"
  };

  const requestParams = {
    ...defaultParams,
    ...params
  };

  try {
    const response = await patientListService.post('trans/findPatient', requestParams);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch patient list:', error);
    throw error;
  }
};

/**
 * Search patients
 * @param {string} searchTerm - search keyword
 * @param {Object} options - other options
 * @returns {Promise} API response
 */
export const searchPatientsApi = async (searchTerm, options = {}) => {
  return getPatientListApi({
    search_patient_field: searchTerm,
    search_patient_type: "patient_name",
    page: "1",
    perPage: "20",
    ...options
  });
};

/**
 * Get patient by patient ID
 * Uses the order API v2: GET {baseUrl}/patient/{patient_id}
 * Production: https://api.vibrant-wellness.com/v2/portal/order/patient/{patient_id}
 * Staging: https://api.vibrant-wellness.com/v2/portal/order-staging/patient/{patient_id}
 * @param {string|number} patientId - The patient ID
 * @returns {Promise<Object|null>} Patient object or null if not found
 */
export const getPatientByIdApi = async (patientId) => {
  try {
    const response = await orderApiV2.get(`/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch patient by ID:', error);
    throw error;
  }
};
